import React, { useEffect, useState } from 'react';
import { useSDK } from '../context/SDKContext.tsx';
import type { AiActRegisterExport, TreatmentRecord } from '@assistia/sdk';
import { ShieldCheck, ShieldAlert, ShieldOff, Shield, Download, Server, Globe } from 'lucide-react';
import AiNotice from '../components/AiNotice.tsx';

export default function AiActRegisterPage(): React.ReactElement {
  const { sdk } = useSDK();
  const [data, setData] = useState<AiActRegisterExport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    sdk.aiAct.getRegister()
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err.message || "Erreur de chargement du registre");
        setLoading(false);
      });
  }, [sdk]);

  const handleDownloadJSON = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aiact_register_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-700 p-4 rounded-md">
          <p className="font-semibold">Erreur de chargement</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      {/* Header */}
      <header className="shrink-0 px-6 py-5 border-b border-border bg-card">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <ShieldCheck className="text-primary" />
              Registre EU AI Act
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Inventaire réglementaire des systèmes d'intelligence artificielle déployés (Article 6 et 50).
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadJSON}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background border border-input hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
            >
              <Download className="mr-2 h-4 w-4" />
              Exporter JSON
            </button>
          </div>
        </div>
      </header>

      {/* Main content scrollable */}
      <main className="flex-1 overflow-y-auto p-6">
        <div className="grid gap-6">
          
          {/* Metadata Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-card rounded-lg border border-border p-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-1">Traitements Actifs</h3>
              <p className="text-2xl font-bold">{data?.treatments_count}</p>
            </div>
            <div className="bg-card rounded-lg border border-border p-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-1">Red Lines (Déclenchées)</h3>
              <p className="text-2xl font-bold text-red-600">{data?.red_lines_count}</p>
            </div>
            <div className="bg-card rounded-lg border border-border p-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-1">Généré le</h3>
              <p className="text-sm font-medium mt-1">
                {data?.generated_at ? new Date(data.generated_at).toLocaleString() : 'N/A'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Framework: {data?.meta?.framework || 'N/A'}
              </p>
            </div>
          </div>

          <AiNotice variant="inline" text="Ce registre est généré dynamiquement à partir de la configuration technique (policy.yaml)." />

          {/* Table of Treatments */}
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-muted/50 border-b border-border text-muted-foreground">
                  <tr>
                    <th className="h-12 px-4 font-medium">ID</th>
                    <th className="h-12 px-4 font-medium">Nom du Traitement</th>
                    <th className="h-12 px-4 font-medium">Catégorie (Risque)</th>
                    <th className="h-12 px-4 font-medium">Données In / Out</th>
                    <th className="h-12 px-4 font-medium">Supervision Humaine</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data?.treatments.map((t: TreatmentRecord) => (
                    <tr key={t.id} className="hover:bg-muted/50 transition-colors">
                      <td className="p-4 font-medium font-mono text-xs">{t.id}</td>
                      <td className="p-4">
                        <div className="font-semibold">{t.name}</div>
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-2" title={t.justification}>
                          {t.justification}
                        </div>
                      </td>
                      <td className="p-4">
                        <RiskBadge category={t.category} />
                        {t.art6_3_self_assessment === 'required' && (
                          <div className="mt-2 text-[10px] uppercase font-bold text-amber-600 bg-amber-100 inline-block px-1.5 py-0.5 rounded">
                            Art 6(3) Requis
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1 text-xs">
                          <span className="text-muted-foreground"><span className="font-medium text-foreground">In:</span> {t.data_in}</span>
                          <span className="text-muted-foreground"><span className="font-medium text-foreground">Out:</span> {t.data_out}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="capitalize">{t.human_oversight.replace('_', ' ')}</span>
                      </td>
                    </tr>
                  ))}
                  {(!data?.treatments || data.treatments.length === 0) && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-muted-foreground">
                        Aucun traitement répertorié.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function RiskBadge({ category }: { category: string }) {
  let colorClass = 'bg-slate-100 text-slate-800 border-slate-200';
  let Icon = Shield;
  let label = category;

  switch (category) {
    case 'minimal':
      colorClass = 'bg-green-100 text-green-800 border-green-200';
      Icon = ShieldCheck;
      label = 'Minimal';
      break;
    case 'limited_transparency':
      colorClass = 'bg-blue-100 text-blue-800 border-blue-200';
      Icon = Shield;
      label = 'Transparence Limitée';
      break;
    case 'high_risk':
      colorClass = 'bg-amber-100 text-amber-800 border-amber-200';
      Icon = ShieldAlert;
      label = 'Haut Risque';
      break;
    case 'prohibited':
      colorClass = 'bg-red-100 text-red-800 border-red-200';
      Icon = ShieldOff;
      label = 'Prohibé';
      break;
  }

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${colorClass}`}>
      <Icon size={12} />
      {label}
    </span>
  );
}
